import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { AuthProvider, useAuth, canSimulate } from "./lib/auth";
import { FlashProvider } from "./lib/flash";
import Layout from "./components/Layout";
import CookieBanner from "./components/CookieBanner";
import { OfflineBanner, UpdatePrompt } from "./components/Pwa";
import { isStandalone } from "./lib/pwa";
import { isLocked } from "./lib/billing";
import LockOverlay from "./components/LockOverlay";
import StaffLogin, { StaffLocked } from "./pages/StaffLogin";
import { syncAds, rememberLanding } from "./lib/googleAds";

rememberLanding();

// Google Ads tag: only on public pages + signup, and only after marketing consent
function AdsTag() {
  const { pathname } = useLocation();
  useEffect(() => {
    syncAds(pathname);
    const on = () => syncAds(window.location.pathname);
    window.addEventListener("cookie-consent-changed", on);
    return () => window.removeEventListener("cookie-consent-changed", on);
  }, [pathname]);
  return null;
}
import Landing from "./pages/Landing";
import PricingPage from "./pages/Pricing";
import FeaturesPage from "./pages/Features";
import { LoginPage, SignupPage, ForgotPage, ResetPage } from "./pages/AuthPages";
import Dashboard from "./pages/Dashboard";
import OfferForm from "./pages/OfferForm";
import RepostReview from "./pages/RepostReview";
import OfferDetail from "./pages/OfferDetail";
import Picklist from "./pages/Picklist";
import Pickup from "./pages/PickupDesk";
import Customers from "./pages/Customers";
import Stats from "./pages/Stats";
import Settings from "./pages/Settings";
import Simulator from "./pages/Simulator";
import Billing from "./pages/Billing";
import Owner from "./pages/Owner";
import CustomerOrder from "./pages/CustomerOrder";
import SupportPage from "./pages/Support";
import StoresPage from "./pages/Stores";
import { PrivacyPage, TermsPage, DpaPage, DeletionPage, DeletionStatusPage } from "./pages/StaticPages";
import "@/App.css";

function FbReturn() {
  return (
    <div className="auth" data-testid="fb-return-page">
      <h1>Facebook-forbindelsen er gennemført</h1>
      <p className="muted">Vend tilbage til JaTak Pro-appen – den opdaterer automatisk, når forbindelsen er klar.</p>
      <div className="actions">
        <button className="btn primary" onClick={() => window.close()} data-testid="fb-return-close">
          Luk denne fane
        </button>
        <a className="btn" href="/login" data-testid="fb-return-login">
          Log ind her
        </a>
      </div>
    </div>
  );
}

function AdminOnly({ children }) {
  const { store } = useAuth();
  return canSimulate(store) ? children : <Navigate to="/overblik" replace />;
}

function Protected({ children }) {
  const { store, epoch } = useAuth();
  const loc = useLocation();
  if (store === undefined) return <div className="loading">Indlæser…</div>;
  if (!store && window.location.pathname === "/settings" && new URLSearchParams(window.location.search).get("fb"))
    return <FbReturn />;
  if (!store) return <Navigate to="/login" replace state={{ from: loc.pathname + loc.search + loc.hash }} />;
  if (store.staff) {
    if (loc.pathname !== "/pickup") return <Navigate to="/pickup" replace />;
    if (isLocked(store)) return <Layout key={epoch}><StaffLocked /></Layout>;
    return <Layout key={epoch}>{children}</Layout>;
  }
  if (isLocked(store) && loc.pathname !== "/billing")
    return <Layout key={epoch}><LockOverlay /></Layout>;
  return <Layout key={epoch}>{children}</Layout>;
}

function Home() {
  const { store } = useAuth();
  if (isStandalone()) {
    if (store === undefined) return <div className="loading">Indlæser…</div>;
    if (store) return <Navigate to="/overblik" replace />;
  }
  return <Landing />;
}

function Overview() {
  const { store } = useAuth();
  if (store?.is_owner) return <Navigate to="/owner" replace />;
  return <Dashboard />;
}

function NotFound() {
  const { store } = useAuth();
  if (store === undefined) return <div className="loading">Indlæser…</div>;
  return <Navigate to={store ? "/overblik" : "/"} replace />;
}

function EditOfferRoute() {
  const { id } = useParams();
  return <OfferForm key={id} editId={Number(id)} />;
}

export default function App() {
  return (
    <AuthProvider>
      <FlashProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/glemt-adgangskode" element={<ForgotPage />} />
            <Route path="/nulstil-adgangskode" element={<ResetPage />} />
            <Route path="/priser" element={<PricingPage />} />
            <Route path="/funktioner" element={<FeaturesPage />} />
            <Route path="/personale" element={<StaffLogin />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/vilkaar" element={<TermsPage />} />
            <Route path="/privatliv" element={<PrivacyPage />} />
            <Route path="/databehandleraftale" element={<DpaPage />} />
            <Route path="/sletning-af-data" element={<DeletionPage />} />
            <Route path="/sletning-af-data/status" element={<DeletionStatusPage />} />
            <Route path="/o/:token" element={<CustomerOrder />} />

            <Route path="/overblik" element={<Protected><Overview /></Protected>} />
            <Route path="/dashboard" element={<Navigate to="/overblik" replace />} />
            <Route path="/offers/new" element={<Protected><OfferForm /></Protected>} />
            <Route path="/offers/:id" element={<Protected><OfferDetail /></Protected>} />
            <Route path="/offers/:id/edit" element={<Protected><EditOfferRoute /></Protected>} />
            <Route path="/offers/:id/picklist" element={<Protected><Picklist /></Protected>} />
            <Route path="/genopslag/:id" element={<Protected><RepostReview /></Protected>} />
            <Route path="/pickup" element={<Protected><Pickup /></Protected>} />
            <Route path="/customers" element={<Protected><Customers /></Protected>} />
            <Route path="/stats" element={<Protected><Stats /></Protected>} />
            <Route path="/settings" element={<Protected><Settings /></Protected>} />
            <Route path="/simulator" element={<Protected><AdminOnly><Simulator /></AdminOnly></Protected>} />
            <Route path="/billing" element={<Protected><Billing /></Protected>} />
            <Route path="/butikker" element={<Protected><StoresPage /></Protected>} />
            <Route path="/owner" element={<Protected><Owner /></Protected>} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          <CookieBanner />
          <AdsTag />
          <OfflineBanner />
          <UpdatePrompt />
        </BrowserRouter>
      </FlashProvider>
    </AuthProvider>
  );
}
